import SimpleDropdown from '@/components/simple-dropdown';
import { useDialog } from '@/contexts/DialogContext';
import { useSnackbar } from '@/contexts/SnackBarContext';
import SponsorService from '@/services/Sponsor.service';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { Form, Formik } from 'formik';
import React, { FC, useCallback, useState } from 'react';
import type { BankAccountResponse } from 'scala-sdk';
import * as yup from 'yup';

interface ManageFeeFormProps {
  sponsorId: number;
  sponsorPlanId: number;
  bankAccounts: BankAccountResponse[];
  feePaymentSourceDetails: {
    availablePaymentSourceTypes: string[];
    selectedPaymentSourceType?: string;
  };
  onRefetch: () => void;
}

const validationSchema = yup.object({
  bankAccountId: yup.string().when('method', {
    is: 'Bank Account',
    otherwise: yup.string(),
    then: yup.string().required('Bank Account is required')
  }),
  method: yup.string().required('Method is required')
});

export const ManageFeeForm: FC<ManageFeeFormProps> = props => {
  const ctx = useDialog();
  const snackbar = useSnackbar();
  const [initialMethod] = useState(
    props.feePaymentSourceDetails?.selectedPaymentSourceType || ''
  );
  const [initialBankAccountId] = useState(
    props.feePaymentSourceDetails?.selectedPaymentSourceType === 'Bank Account'
      ? props.bankAccounts?.filter(b => b.isAssignedForFeePayments)?.[0]
          ?.bankAccountId
      : ''
  );

  const postFeePaymentMethod = useMutation(
    [
      'SponsorService.postFeePaymentMethod',
      props.sponsorId,
      props.sponsorPlanId
    ],
    (data: { bankAccountId?: number; paymentSourceType: string }) =>
      SponsorService.postFeePaymentMethod({
        ...{ bankAccountId: data.bankAccountId ? data.bankAccountId : null },
        paymentSourceType: data.paymentSourceType,
        sponsorId: +props.sponsorId,
        sponsorPlanId: props.sponsorPlanId
      }),
    {
      onError: (err: any) => {
        snackbar.showSnackbar({
          message: err.message,
          severity: 'error'
        });
      },
      onSuccess: async () => {
        ctx.closeDialog();
        props.onRefetch();
        snackbar.showSnackbar({
          message: 'Fee payment method has successfully been updated.',
          severity: 'success'
        });
      }
    }
  );

  const onSubmit = useCallback(async values => {
    postFeePaymentMethod.mutate({
      bankAccountId: +values.bankAccountId,
      paymentSourceType: values.method
    });
  }, []);

  return (
    <Formik
      initialValues={{
        bankAccountId: initialBankAccountId,
        method: initialMethod
      }}
      onSubmit={onSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      validationSchema={validationSchema}>
      {({ setValues, values, isValid, setFieldValue }) => {
        return (
          <Form>
            <Grid
              container
              display='flex'
              flexDirection='column'
              justifyContent='space-between'>
              <Grid item xl={12}>
                <DialogTitle>Manage Fee Payment Method</DialogTitle>
              </Grid>
              <Grid item xl={12}>
                <DialogContent data-testid='manageFeePaymentMethodModalContent'>
                  <Grid
                    container
                    display='flex'
                    flexDirection='column'
                    item
                    justifyContent='space-between'
                    spacing={2}
                    xl={12}>
                    <Grid item xl={12}>
                      <SimpleDropdown
                        fieldId='method'
                        fieldName='Method'
                        fieldValues={
                          props.feePaymentSourceDetails
                            ?.availablePaymentSourceTypes
                        }
                        onChange={value => {
                          setValues({
                            ...values,
                            bankAccountId:
                              value.target.value === 'Bank Account'
                                ? values.bankAccountId
                                : '',
                            method: value.target.value
                          });
                        }}
                        size='small'
                      />
                    </Grid>
                    <Grid item xl={12}>
                      {values.method === 'Credit Card' ? (
                        <Alert severity='info'>
                          Please ensure the sponsor is notified that a{' '}
                          <b>3% processing fee</b> will apply to all fee
                          payments made via credit card.
                        </Alert>
                      ) : values.method === 'Check' ? (
                        <Alert severity='info'>
                          All checks should be mailed to the following address:{' '}
                          <br />
                          <b>
                            360 Madison Avenue, 15th Floor New York, NY 10017
                          </b>
                        </Alert>
                      ) : values.method === 'E-Pay' ? (
                        <Alert severity='info'>
                          Please reach out to the billing team if you have any
                          questions about setting up E-Pay.
                        </Alert>
                      ) : values.method === 'Bank Account' ? (
                        <FormControl>
                          <FormLabel
                            id='bankAccountId'
                            sx={{
                              marginBottom: 2
                            }}>
                            Select an account
                          </FormLabel>
                          <RadioGroup
                            name='bankAccountId'
                            onChange={e =>
                              setFieldValue('bankAccountId', e.target.value)
                            }
                            value={values.bankAccountId || ''}>
                            {props.bankAccounts?.map(b => (
                              <FormControlLabel
                                control={<Radio />}
                                key={b.bankAccountId}
                                label={
                                  <Grid item>
                                    <Typography variant='body1'>
                                      {b.bankName}
                                    </Typography>
                                    <Typography variant='body2'>
                                      Routing Number: {b.routingNumber} |
                                      Account Number: {b.accountNumber}
                                    </Typography>
                                  </Grid>
                                }
                                sx={{ marginBottom: 2 }}
                                value={b.bankAccountId}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <Typography>
                          Credit card or check method on older plans are not
                          stored.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </DialogContent>
              </Grid>
              <Grid item marginTop={2} xl={12}>
                <DialogActions>
                  <Button
                    data-testid='cancelButton'
                    onClick={ctx.closeDialog}
                    variant='text'>
                    CANCEL
                  </Button>
                  <Button
                    data-testid='submitButton'
                    disabled={
                      !isValid ||
                      postFeePaymentMethod.isLoading ||
                      (values.method === initialMethod &&
                        (values.method !== 'Bank Account' ||
                          +values.bankAccountId === initialBankAccountId))
                    }
                    type='submit'
                    variant='contained'>
                    UPDATE
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
